import {
    AvailablePlanType,
    CalculatorResultType,
    PayerCoverageCheckNetwork,
} from "@common-shared/config/coverage.config"
import {
    OneVerifyFlagType,
    QuotaUsageType,
} from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { COMMON_ONE_VERIFY_GENERAL_CONFIG } from "@common-shared/config/oneVerify/oneVerifyGeneral.config"
import type { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import {
    BULK_RESULTS_INSIGHTS_CONFIG,
    BulkPlanFinding,
    BulkResponsibilityBucket,
    BulkRunOutcome,
} from "../config/bulkResultsInsights.config"
import type {
    IBulkResultsCountItem,
    IBulkResultsFilter,
    IBulkResultsFindingStat,
    IBulkResultsInsightsModel,
    IBulkResultsOutcomeStat,
    IBulkResultsPatientRow,
    IBulkResultsResponsibilityStat,
    IBulkResultsRowAggregate,
} from "../types/bulkResultsInsights.types"
import type { IDynamicCheckerFormInputData } from "../types/data.types"
import type {
    IContinuousMonitoringCoverageCheck,
    IMedicaidCoverageCheck,
} from "../types/objects.types"
import { CoverageHistoryHelper } from "./coverageHistory.helper"
import { UtilHelper } from "./util.helper"

/**
 * Maps bulk checker Redux results into the insights view model and filters.
 */
export class BulkResultsInsightsHelper {
    /**
     * Build the insights model from completed checks in the current bulk run
     */
    static buildModel(
        checks: IContinuousMonitoringCoverageCheck[] | IMedicaidCoverageCheck[],
        checkType: QuotaUsageType,
        isMedicaid: boolean,
    ): IBulkResultsInsightsModel {
        const rows: IBulkResultsPatientRow[] = []

        // Rows that failed CSV validation are never submitted, so they can
        // never complete and must stay out of the run total
        let submittedCount = 0

        for (const check of checks) {
            if (!check.inputData.csvValidationErrors?.length) {
                submittedCount++
            }

            const row = this.toPatientRow(
                check,
                this.getPreferredHistory(check, isMedicaid),
            )

            if (row) {
                rows.push(row)
            }
        }

        const completedCount = rows.length

        const creditsPerCheck =
            COMMON_ONE_VERIFY_GENERAL_CONFIG.quotaUsageAmounts[checkType] || 1

        const aggregate = this.aggregateRows(rows)

        return {
            rows,
            totalPatients: Math.max(submittedCount, completedCount),
            completedCount,
            creditsUsed: completedCount * creditsPerCheck,
            finishedAt: aggregate.finishedAt || null,
            fullCoverageCount: aggregate.fullCoverageCount,
            inDeductibleCount: aggregate.inDeductibleCount,
            secondPassCount: Math.max(
                0,
                completedCount - aggregate.fullCoverageCount,
            ),
            inactiveCount: aggregate.inactiveCount,
            smartScanRecoveredCount: aggregate.smartScanRecoveredCount,
            outcomes: this.buildOutcomeStats(
                aggregate.outcomeCounts,
                completedCount,
            ),
            responsibility: this.buildResponsibilityStats(
                aggregate.responsibilityGroups,
            ),
            findings: this.buildFindingStats(aggregate.findingGroups),
        }
    }

    /**
     * Return result rows matching the active outcome, responsibility, or finding filter
     */
    static getFilteredRows(
        rows: IBulkResultsPatientRow[],
        filter: IBulkResultsFilter | null,
    ): IBulkResultsPatientRow[] {
        if (!filter) {
            return rows
        }

        return rows.filter(row => {
            switch (filter.kind) {
                case "outcome":
                    return row.outcome === filter.value

                case "responsibility":
                    return this.getResponsibilityBucket(row) === filter.value

                case "finding": {
                    if (
                        !row.findings.includes(filter.value as BulkPlanFinding)
                    ) {
                        return false
                    }

                    if (!filter.subtype) {
                        return true
                    }

                    return (
                        this.getFindingSubtype(
                            row,
                            filter.value as BulkPlanFinding,
                        ) === filter.subtype
                    )
                }

                default:
                    return true
            }
        })
    }

    /**
     * Collect the original input data behind the given rows, to re-run those checks
     * ZIP is prefilled from the row's result, since standard and medicaid CSVs don't have it
     */
    static getInputDataForRows(
        checks: IContinuousMonitoringCoverageCheck[] | IMedicaidCoverageCheck[],
        rows: IBulkResultsPatientRow[],
        isMedicaid: boolean,
    ): IDynamicCheckerFormInputData[] {
        const rowIds = new Set(rows.map(row => row.id))
        const result: IDynamicCheckerFormInputData[] = []

        for (const check of checks) {
            if (!rowIds.has(check.inputData.id)) {
                continue
            }

            result.push({
                ...check.inputData,

                zipCode:
                    UtilHelper.normalizeZipCode(check.inputData.zipCode) ||
                    CoverageHistoryHelper.getSmartScanZipCode(
                        this.getPreferredHistory(check, isMedicaid)
                            ?.coverageResult,
                    ),
            })
        }

        return result
    }

    /**
     * Classify a full-coverage row into a patient-responsibility bucket, or null otherwise
     */
    static getResponsibilityBucket(
        row: IBulkResultsPatientRow,
    ): BulkResponsibilityBucket | null {
        if (row.outcome !== BulkRunOutcome.FULL_COVERAGE) {
            return null
        }

        if ((row.remainingDeductibleCents || 0) > 0) {
            return BulkResponsibilityBucket.IN_DEDUCTIBLE
        }

        if ((row.copaymentCents || 0) > 0) {
            return BulkResponsibilityBucket.COPAY
        }

        if ((row.coinsurancePercent || 0) > 0) {
            return BulkResponsibilityBucket.COINSURANCE
        }

        return BulkResponsibilityBucket.FULLY_COVERED
    }

    /**
     * Get the history a row is built from
     * Medicaid checks have a single result, others prefer in-network then out-of-network
     */
    private static getPreferredHistory(
        check: IContinuousMonitoringCoverageCheck | IMedicaidCoverageCheck,
        isMedicaid: boolean,
    ): IOneVerifyCoverageCheckHistory | null {
        if (isMedicaid) {
            return check.result as IOneVerifyCoverageCheckHistory | null
        }

        const { result } = check as IContinuousMonitoringCoverageCheck

        return (
            result?.[PayerCoverageCheckNetwork.IN] ||
            result?.[PayerCoverageCheckNetwork.OUT] ||
            null
        )
    }

    /**
     * Convert one completed check into a results-table row, or null if it has no result yet
     */
    private static toPatientRow(
        check: IContinuousMonitoringCoverageCheck | IMedicaidCoverageCheck,
        history: IOneVerifyCoverageCheckHistory | null,
    ): IBulkResultsPatientRow | null {
        if (!history?.coverageResult?.resultType) {
            return null
        }

        const { coverageResult } = history
        const input = check.inputData
        const outcome = this.mapOutcome(
            coverageResult.resultType,
            coverageResult.flags,
        )
        const findings = this.mapFindings(coverageResult)
        const tpaName = coverageResult.thirdPartyAdministrator?.name || null
        const additionalPolicySubtype =
            this.mapAdditionalPolicySubtype(coverageResult)
        const medicaidSubtype = this.mapMedicaidSubtype(coverageResult.planType)
        const medicareSubtype = this.mapMedicareSubtype(coverageResult)
        const oonSubtype = this.mapOonSubtype(coverageResult)

        return {
            id: input.id,
            patientName:
                `${input.firstName || ""} ${input.lastName || ""}`.trim(),
            payerName:
                coverageResult.payer?.insuranceName ||
                history.requestPayer?.insuranceName ||
                input.payer?.insuranceName ||
                "—",
            outcome,
            costShareLabel: this.formatCostShare(coverageResult, outcome),
            findings,
            findingTags: findings.map(finding => ({
                finding,
                label: BULK_RESULTS_INSIGHTS_CONFIG.findingTagLabels[finding],
            })),
            remainingDeductibleCents: coverageResult.remainingDeductible,
            copaymentCents: coverageResult.copayment,
            coinsurancePercent: coverageResult.coinsurance,
            memberObligationCents: coverageResult.memberObligation,
            createdAt: coverageResult.createdAt,
            tpaName,
            additionalPolicySubtype,
            medicaidSubtype,
            medicareSubtype,
            oonSubtype,
            isSmartScanRecovered: !!coverageResult.isSmartScanResult,
        }
    }

    /**
     * Map a coverage result type and flags onto a single mutually exclusive run outcome
     */
    private static mapOutcome(
        resultType: CalculatorResultType,
        flags: OneVerifyFlagType[] = [],
    ): BulkRunOutcome {
        if (resultType === CalculatorResultType.activeCoverage) {
            return BulkRunOutcome.FULL_COVERAGE
        }

        if (resultType === CalculatorResultType.activePartialCoverage) {
            return BulkRunOutcome.COST_DETAILS_UNAVAILABLE
        }

        if (resultType === CalculatorResultType.noCoverage) {
            return BulkRunOutcome.POLICY_INACTIVE
        }

        if (resultType === CalculatorResultType.memberNotFound) {
            return BulkRunOutcome.MEMBER_NOT_FOUND
        }

        if (flags.includes(OneVerifyFlagType.CREDENTIALING_ERROR)) {
            return BulkRunOutcome.CREDENTIALING_ISSUE
        }

        if (flags.includes(OneVerifyFlagType.ENROLLMENT_REQUIRED)) {
            return BulkRunOutcome.ENROLLMENT_INCOMPLETE
        }

        if (resultType === CalculatorResultType.providerNotRecognized) {
            return BulkRunOutcome.CREDENTIALING_ISSUE
        }

        return BulkRunOutcome.PAYER_UNAVAILABLE
    }

    /**
     * Collect every plan finding that applies to a coverage result; a row may have several
     */
    private static mapFindings(
        coverageResult: IOneVerifyCoverageCheckHistory["coverageResult"],
    ): BulkPlanFinding[] {
        const findings: BulkPlanFinding[] = []
        const flags = coverageResult.flags || []

        if (
            flags.includes(OneVerifyFlagType.THIRD_PARTY_PAYER_DETECTED) ||
            coverageResult.thirdPartyAdministrator?.name
        ) {
            findings.push(BulkPlanFinding.TPA)
        }

        if (
            flags.includes(OneVerifyFlagType.ADDITIONAL_POLICY_DETECTED) ||
            coverageResult.additionalPolicy
        ) {
            findings.push(BulkPlanFinding.ADDITIONAL_POLICY)
        }

        if (
            coverageResult.planType === AvailablePlanType.MANAGED_MEDICAID ||
            coverageResult.planType === AvailablePlanType.ORIGINAL_MEDICAID
        ) {
            findings.push(BulkPlanFinding.MEDICAID)
        }

        if (
            coverageResult.planType === AvailablePlanType.MEDICARE_ADVANTAGE ||
            coverageResult.planType ===
                AvailablePlanType.SUPPLEMENTAL_MEDICARE ||
            coverageResult.planType === AvailablePlanType.ORIGINAL_MEDICARE ||
            coverageResult.planType === AvailablePlanType.DUAL ||
            coverageResult.qmbStatus ||
            flags.includes(OneVerifyFlagType.QUALIFIED_MEDICARE_BENEFICIARY)
        ) {
            findings.push(BulkPlanFinding.MEDICARE)
        }

        if (coverageResult.networkStatusAlert?.isAlert) {
            findings.push(BulkPlanFinding.OUT_OF_NETWORK)
        }

        return findings
    }

    /**
     * Classify an additional policy as commercial, Medicare, or unidentified
     */
    private static mapAdditionalPolicySubtype(
        coverageResult: IOneVerifyCoverageCheckHistory["coverageResult"],
    ): string | null {
        const additionalPolicy = coverageResult.additionalPolicy

        if (!additionalPolicy) {
            return null
        }

        if (!additionalPolicy.insurer) {
            return "unidentified"
        }

        const medicareTypes = [
            AvailablePlanType.MEDICARE_ADVANTAGE,
            AvailablePlanType.ORIGINAL_MEDICARE,
            AvailablePlanType.SUPPLEMENTAL_MEDICARE,
            AvailablePlanType.DUAL,
        ]

        if (medicareTypes.includes(additionalPolicy.planType)) {
            return "medicare"
        }

        return "commercial"
    }

    /**
     * Classify Medicaid as managed or fee-for-service, or null when the plan is not Medicaid
     */
    private static mapMedicaidSubtype(
        planType: AvailablePlanType,
    ): string | null {
        if (planType === AvailablePlanType.MANAGED_MEDICAID) {
            return "managed"
        }

        if (planType === AvailablePlanType.ORIGINAL_MEDICAID) {
            return "ffs"
        }

        return null
    }

    /**
     * Classify Medicare as Advantage, supplemental, MSP/QMB, or original, or null otherwise
     */
    private static mapMedicareSubtype(
        coverageResult: IOneVerifyCoverageCheckHistory["coverageResult"],
    ): string | null {
        if (coverageResult.planType === AvailablePlanType.MEDICARE_ADVANTAGE) {
            return "advantage"
        }

        if (
            coverageResult.planType === AvailablePlanType.SUPPLEMENTAL_MEDICARE
        ) {
            return "supplemental"
        }

        if (
            coverageResult.planType === AvailablePlanType.DUAL ||
            coverageResult.qmbStatus ||
            coverageResult.flags?.includes(
                OneVerifyFlagType.QUALIFIED_MEDICARE_BENEFICIARY,
            )
        ) {
            return "msp"
        }

        if (coverageResult.planType === AvailablePlanType.ORIGINAL_MEDICARE) {
            return "original"
        }

        return null
    }

    /**
     * Classify out-of-network as excluded by a practice rule or not contracted, or null.
     * isInNetworkCheck is which benefits the user requested, not an OON finding.
     */
    private static mapOonSubtype(
        coverageResult: IOneVerifyCoverageCheckHistory["coverageResult"],
    ): string | null {
        if (!coverageResult.networkStatusAlert?.isAlert) {
            return null
        }

        if (coverageResult.networkStatusAlert.rule) {
            return "excludedByRule"
        }

        return "notContracted"
    }

    /**
     * Build the cost-share cell copy for a full-coverage result, or an em dash otherwise
     */
    private static formatCostShare(
        coverageResult: IOneVerifyCoverageCheckHistory["coverageResult"],
        outcome: BulkRunOutcome,
    ): string {
        if (outcome !== BulkRunOutcome.FULL_COVERAGE) {
            return "—"
        }

        const remainingDeductible = coverageResult.remainingDeductible || 0
        const copayment = coverageResult.copayment || 0
        const coinsurance = coverageResult.coinsurance || 0
        const memberObligation = coverageResult.memberObligation

        if (remainingDeductible > 0) {
            const due =
                memberObligation != null
                    ? ` · ${UtilHelper.getFormattedMoney(memberObligation, true)} due`
                    : ""

            return `${UtilHelper.getFormattedMoney(remainingDeductible, true)} deductible left${due}`
        }

        if (copayment > 0) {
            return `${UtilHelper.getFormattedMoney(copayment, true)} copay`
        }

        if (coinsurance > 0) {
            const due =
                memberObligation != null
                    ? ` · ${UtilHelper.getFormattedMoney(memberObligation, true)} due`
                    : ""

            return `${coinsurance}% coinsurance${due}`
        }

        return "Fully covered"
    }

    /**
     * Return the subtype key used to filter or break down a specific finding on a row
     */
    private static getFindingSubtype(
        row: IBulkResultsPatientRow,
        finding: BulkPlanFinding,
    ): string | null {
        switch (finding) {
            case BulkPlanFinding.TPA:
                return row.tpaName
            case BulkPlanFinding.ADDITIONAL_POLICY:
                return row.additionalPolicySubtype
            case BulkPlanFinding.MEDICAID:
                return row.medicaidSubtype
            case BulkPlanFinding.MEDICARE:
                return row.medicareSubtype
            case BulkPlanFinding.OUT_OF_NETWORK:
                return row.oonSubtype
            default:
                return null
        }
    }

    /**
     * Walk the rows once and collect every count and grouping the stat builders need
     */
    private static aggregateRows(
        rows: IBulkResultsPatientRow[],
    ): IBulkResultsRowAggregate {
        const aggregate: IBulkResultsRowAggregate = {
            outcomeCounts: {},
            responsibilityGroups: {
                [BulkResponsibilityBucket.IN_DEDUCTIBLE]: [],
                [BulkResponsibilityBucket.COPAY]: [],
                [BulkResponsibilityBucket.COINSURANCE]: [],
                [BulkResponsibilityBucket.FULLY_COVERED]: [],
            },
            findingGroups: {
                [BulkPlanFinding.TPA]: [],
                [BulkPlanFinding.ADDITIONAL_POLICY]: [],
                [BulkPlanFinding.MEDICAID]: [],
                [BulkPlanFinding.MEDICARE]: [],
                [BulkPlanFinding.OUT_OF_NETWORK]: [],
            },
            fullCoverageCount: 0,
            inDeductibleCount: 0,
            inactiveCount: 0,
            smartScanRecoveredCount: 0,
            finishedAt: 0,
        }

        for (const row of rows) {
            aggregate.outcomeCounts[row.outcome] =
                (aggregate.outcomeCounts[row.outcome] || 0) + 1

            // getResponsibilityBucket only buckets full coverage rows, so the
            // other outcomes can skip it entirely
            if (row.outcome === BulkRunOutcome.FULL_COVERAGE) {
                aggregate.fullCoverageCount++

                const bucket = this.getResponsibilityBucket(row)

                if (bucket) {
                    aggregate.responsibilityGroups[bucket].push(row)

                    if (bucket === BulkResponsibilityBucket.IN_DEDUCTIBLE) {
                        aggregate.inDeductibleCount++
                    }
                }
            } else if (row.outcome === BulkRunOutcome.POLICY_INACTIVE) {
                aggregate.inactiveCount++
            }

            if (row.isSmartScanRecovered) {
                aggregate.smartScanRecoveredCount++
            }

            if ((row.createdAt || 0) > aggregate.finishedAt) {
                aggregate.finishedAt = row.createdAt
            }

            for (const finding of row.findings) {
                aggregate.findingGroups[finding].push(row)
            }
        }

        return aggregate
    }

    /**
     * Count rows per run outcome in display order, including outcomes with a zero count
     */
    private static buildOutcomeStats(
        counts: Partial<Record<BulkRunOutcome, number>>,
        totalRows: number,
    ): IBulkResultsOutcomeStat[] {
        return BULK_RESULTS_INSIGHTS_CONFIG.outcomeOrder.map(outcome => {
            const count = counts[outcome] || 0

            return {
                key: outcome,
                label: BULK_RESULTS_INSIGHTS_CONFIG.outcomeLabels[outcome],
                count,
                color: BULK_RESULTS_INSIGHTS_CONFIG.outcomeColors[outcome],
                percentLabel: this.formatPercent(count, totalRows),
                actionLabel:
                    BULK_RESULTS_INSIGHTS_CONFIG.outcomeActions[outcome],
                isAction:
                    BULK_RESULTS_INSIGHTS_CONFIG.outcomeActionIsLink[outcome],
            }
        })
    }

    /**
     * Count full-coverage rows per responsibility bucket and attach summary detail copy
     */
    private static buildResponsibilityStats(
        grouped: Record<BulkResponsibilityBucket, IBulkResultsPatientRow[]>,
    ): IBulkResultsResponsibilityStat[] {
        return BULK_RESULTS_INSIGHTS_CONFIG.responsibilityOrder.map(bucket => {
            const bucketRows = grouped[bucket]

            return {
                key: bucket,
                label: BULK_RESULTS_INSIGHTS_CONFIG.responsibilityLabels[
                    bucket
                ],
                count: bucketRows.length,
                detail: this.formatResponsibilityDetail(bucket, bucketRows),
                description:
                    BULK_RESULTS_INSIGHTS_CONFIG.responsibilityDescriptions[
                        bucket
                    ],
            }
        })
    }

    /**
     * Build the average/median detail line shown on a responsibility card
     */
    private static formatResponsibilityDetail(
        bucket: BulkResponsibilityBucket,
        rows: IBulkResultsPatientRow[],
    ): string {
        if (!rows.length) {
            return ""
        }

        switch (bucket) {
            case BulkResponsibilityBucket.IN_DEDUCTIBLE: {
                const avg = this.average(
                    rows.map(row => row.remainingDeductibleCents || 0),
                )

                return `Avg ${UtilHelper.getFormattedMoney(avg, true)} remaining`
            }

            case BulkResponsibilityBucket.COPAY: {
                const median = this.median(
                    rows.map(row => row.copaymentCents || 0),
                )

                return `Median ${UtilHelper.getFormattedMoney(median, true)} per session`
            }

            case BulkResponsibilityBucket.COINSURANCE: {
                const median = this.median(
                    rows.map(row => row.coinsurancePercent || 0),
                )

                return `Median ${median}% of session rate`
            }

            case BulkResponsibilityBucket.FULLY_COVERED:
                return "No patient responsibility"
        }
    }

    /**
     * Count rows per plan finding in display order and attach a subtype breakdown
     */
    private static buildFindingStats(
        grouped: Record<BulkPlanFinding, IBulkResultsPatientRow[]>,
    ): IBulkResultsFindingStat[] {
        return BULK_RESULTS_INSIGHTS_CONFIG.findingOrder.map(finding => {
            const matching = grouped[finding]

            return {
                key: finding,
                label: BULK_RESULTS_INSIGHTS_CONFIG.findingLabels[finding],
                count: matching.length,
                icon: BULK_RESULTS_INSIGHTS_CONFIG.findingIcons[finding],
                isWarning: finding === BulkPlanFinding.OUT_OF_NETWORK,
                breakdown: this.buildFindingBreakdown(finding, matching),
            }
        })
    }

    /**
     * Group matching rows by finding subtype and sort the breakdown by count descending
     */
    private static buildFindingBreakdown(
        finding: BulkPlanFinding,
        rows: IBulkResultsPatientRow[],
    ): IBulkResultsCountItem[] {
        const counts: Record<string, number> = {}

        rows.forEach(row => {
            const subtype = this.getFindingSubtype(row, finding) || "other"
            counts[subtype] = (counts[subtype] || 0) + 1
        })

        const labelMap = this.getFindingSubtypeLabels(finding)

        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([key, count]) => ({
                key,
                label: labelMap[key] || key,
                count,
            }))
    }

    /**
     * Look up display labels for a finding's subtype keys
     */
    private static getFindingSubtypeLabels(
        finding: BulkPlanFinding,
    ): Record<string, string> {
        switch (finding) {
            case BulkPlanFinding.ADDITIONAL_POLICY:
                return BULK_RESULTS_INSIGHTS_CONFIG.additionalPolicySubtypeLabels
            case BulkPlanFinding.MEDICAID:
                return BULK_RESULTS_INSIGHTS_CONFIG.medicaidSubtypeLabels
            case BulkPlanFinding.MEDICARE:
                return BULK_RESULTS_INSIGHTS_CONFIG.medicareSubtypeLabels
            case BulkPlanFinding.OUT_OF_NETWORK:
                return BULK_RESULTS_INSIGHTS_CONFIG.oonSubtypeLabels
            default:
                return {}
        }
    }

    /**
     * Format a count as a percent of total, with one decimal when it is not a whole number
     */
    private static formatPercent(count: number, total: number): string {
        if (!total) {
            return "0%"
        }

        const percent = (count / total) * 100

        return `${Number.isInteger(percent) ? percent : percent.toFixed(1)}%`
    }

    /**
     * Return the rounded arithmetic mean of the given values, or 0 for an empty list
     */
    private static average(values: number[]): number {
        if (!values.length) {
            return 0
        }

        return Math.round(
            values.reduce((sum, value) => sum + value, 0) / values.length,
        )
    }

    /**
     * Return the rounded median of the given values, or 0 for an empty list
     */
    private static median(values: number[]): number {
        if (!values.length) {
            return 0
        }

        const sorted = [...values].sort((a, b) => a - b)
        const mid = Math.floor(sorted.length / 2)

        if (sorted.length % 2) {
            return sorted[mid]
        }

        return Math.round((sorted[mid - 1] + sorted[mid]) / 2)
    }
}
